import {Trans} from '@lingui/macro';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Handler, RendererOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {openInBrowser} from '../../../../../srcCommon/browser/openInBrowser';
import {CrmSyncSourcesEnum} from '../../../../models/api/crmSyncModel';
import {IconBox} from '../../../common/iconBox/iconBox';
import {computeHelpCenterLink} from './crmSyncHelpers';

/*
 * Props.
 */

interface CrmSyncInfoBoxProps {
  isAdmin: boolean;
  title: ReactNode;
  crmSyncSource: CrmSyncSourcesEnum;
  renderBody?: RendererOf<ReactNode>;
  onRequestClose: Handler;
}

/*
 * Style.
 */

const StyledIconBox = styled(IconBox)`
  width: 100%;
`;

const StyledBodyDiv = styled.div`
  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.normal};
  line-height: 16px;
  margin-top: 8px;
`;

const StyledContentDiv = styled.div`
  display: flex;
  flex-flow: column;
`;

const StyledTitleDiv = styled.div`
  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.small};
  font-weight: ${fontWeights.semibold};
  line-height: 16px;
`;

const StyledButton = styled(Button)`
  width: fit-content;
  padding-top: 6px;
  margin-left: -14px;
`;

const StyledButtonContentDiv = styled.div`
  display: flex;
  flex-flow: row;
  justify-content: center;
  font-weight: ${fontWeights.bold};
  font-size: ${fontSizes.tiny};
  align-items: center;
`;

const StyledButtonIcon = styled(Icon)`
  margin-left: 6px;
`;

const StyledOnlyAdminWTextWrapperDiv = styled.div`
  margin-top: 10px;
`;

/*
 * Component.
 */

export const CrmSyncInfoBox: FC<CrmSyncInfoBoxProps> = (props) => {
  const theme = useUserTheme();
  const {isAdmin, title, crmSyncSource, renderBody, onRequestClose} = props;
  return (
    <StyledIconBox
      onRequestClose={onRequestClose}
      iconColor={theme.palette.blue.shade40}
      iconName="infoFilledSmall"
    >
      <StyledContentDiv>
        <StyledTitleDiv>{title}</StyledTitleDiv>
        <StyledBodyDiv>{renderMessageBody(isAdmin, renderBody)}</StyledBodyDiv>
        <StyledButton
          onClick={() => openInBrowser(computeHelpCenterLink(crmSyncSource))}
          size={VisualSizesEnum.MEDIUM}
          variant={ButtonVariantsEnum.LINK}
        >
          <StyledButtonContentDiv>
            <Trans id="accounts-crm-dialog-infobox-learn-more">LEARN MORE</Trans>
            <StyledButtonIcon name="externalLinkSmall" />
          </StyledButtonContentDiv>
        </StyledButton>
      </StyledContentDiv>
    </StyledIconBox>
  );
};

/*
 * Helpers.
 */

function renderDefaultBody(isAdmin: boolean) {
  return (
    <>
      <Trans id="accounts-crm-dialog-infobox-description-1">
        Your accounts will be synced once every day to keep them up to date in Front
      </Trans>{' '}
      <span role="img" aria-label="party-popper-emoji">
        🎉
      </span>
      <br />
      {renderAdditionalInformation(isAdmin)}
    </>
  );
}

function renderMessageBody(isAdmin: boolean, renderBody?: RendererOf<ReactNode>) {
  const defaultBody = renderDefaultBody(isAdmin);
  return renderBody ? renderBody(defaultBody) : defaultBody;
}

function renderAdditionalInformation(isAdmin: boolean) {
  if (isAdmin) {
    return (
      <Trans id="accounts-crm-dialog-infobox-description-2">
        You can also manually trigger a sync whenever you need.
      </Trans>
    );
  }
  return (
    <>
      <Trans id="accounts-crm-dialog-infobox-description-non-admin-1">
        Your administrators can also manually trigger a sync whenever needed.
      </Trans>
      <StyledOnlyAdminWTextWrapperDiv>
        <Trans id="accounts-crm-dialog-infobox-description-non-admin-2">
          Only Front administrators can make edits to this sync.
        </Trans>
      </StyledOnlyAdminWTextWrapperDiv>
    </>
  );
}
